import React, { useEffect, useState, useCallback } from 'react';

import { viewParticipant } from '../../../../services/ParticipantService';

import { BackButton } from '../../../common/button/back-button/BackButton';
import { BreadCrumbTrail } from '../../../common/breadcrumb-trail/BreadcrumbTrail';

import { ParticipantClientDetails } from './ParticipantClientDetails';
import { ParticipantStudyDetails } from './ParticipantStudyDetails';
import { ParticipantVisitsTab } from './visits/ParticipantVisitsTab';

import './ParticipantEditTab.css';

export const ParticipantsEditTab = ({
  context,
  setContext,
  navigateTo,
  showToaster,
  setSpinnerText,
  toolTipsEnabled,
  navigateBack,
  navigateToWith,
}) => {
  const [buttonPressed, setButtonPressed] = useState(false);
  const [activeTab, setActiveTab] = useState('participantClientDetails');
  const [participant, setParticipant] = useState({});
  const [currentScreen, setCurrentScreen] = useState('participantClientDetails');
  const [participantStudyId, setParticipantStudyId] = useState(null);
  const [participantStudyIdType, setParticipantStudyIdType] = useState(null);
  const [activeStudy, setActiveStudy] = useState(null);
  const [lastFollowUpDate, setLastFollowUpDate] = useState(null);

  const retrieveParticipant = useCallback(
    async (username, externalReference) => {
      setSpinnerText('Retrieving Participant');
      const [data, error] = await viewParticipant(username, externalReference);
      if (data) {
        if (data.responseStatus === 'Success') {
          setParticipant(data.participant);
          showToaster({
            responseStatus: 'Success',
            responseMessage: 'Participant Information Retrieved',
          });
        } else {
          showToaster(data);
        }
      } else {
        showToaster({
          responseStatus: 'Error',
          responseMessage: error.message,
        });
      }
      setSpinnerText(null);
    },
    [setSpinnerText, showToaster]
  );

  useEffect(() => {
    if (context.additionalContext) {
      if (context.additionalContext.participantExternalReference) {
        retrieveParticipant(context.username, context.additionalContext.participantExternalReference);
      } else {
        retrieveParticipant(context.username, context.additionalContext);
      }
    }
  }, [context.additionalContext, context.username, retrieveParticipant]);

  useEffect(() => {
    if (participant) {
      setParticipantStudyId(participant?.id || null);
      setParticipantStudyIdType(participant?.idType || null);
      setActiveStudy(participant?.activeStudy || null);
      setLastFollowUpDate(participant?.lastFollowUpDate || null);
    }
  }, [participant]);

  const screenMap = new Map();
  screenMap.set(
    'participantClientDetails',
    <ParticipantClientDetails
      context={context}
      participant={participant}
      setParticipant={setParticipant}
      navigateTo={navigateTo}
      navigateToWith={navigateToWith}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
    />
  );
  screenMap.set('participantStudyDetails', <ParticipantStudyDetails participant={participant} />);

  screenMap.set(
    'participantVisits',
    <ParticipantVisitsTab
      context={context}
      setContext={setContext}
      participant={participant}
      setParticipant={setParticipant}
      toolTipsEnabled={toolTipsEnabled}
      setSpinnerText={setSpinnerText}
      showToaster={showToaster}
    />
  );

  const handleNavigationClick = (tab) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
    setButtonPressed(true);
  };

  return (
    <div className="clients-create-container">
      <div className="clients-create-header-container">
        <BackButton onClick={navigateBack} />
        <h2>Edit / View Participant</h2>
      </div>

      <div className="participant-details">
        <div>
          <label className="input-label">ID: </label>
          <label>{participantStudyId || '-'}</label>
        </div>
        <div>
          <label className="input-label">ID Type: </label>
          <label>{participantStudyIdType || '-'}</label>
        </div>
        <div>
          <label className="input-label">Active Study: </label>
          <label>{activeStudy || '-'}</label>
        </div>
        <div>
          <label className="input-label">Last Follow-up Date: </label>
          <label>{lastFollowUpDate || '-'}</label>
        </div>
      </div>

      <div className="wizard-content-container">
        <div className="wizard-main-content-container">
          <div className="clients-create-container">
            <div className="clients-create-tab-container">
              <div
                className={
                  'clients-create-tab client-details ' +
                  (activeTab === 'participantClientDetails' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('participantClientDetails')}
              >
                Client Details
              </div>
              <div
                className={
                  'clients-create-tab study-details ' + (activeTab === 'participantStudyDetails' ? 'active-tab' : null)
                }
                onClick={() => handleNavigationClick('participantStudyDetails')}
              >
                Study Details
              </div>
              <div
                className={'clients-create-tab visits ' + (activeTab === 'participantVisits' ? 'active-tab' : null)}
                onClick={() => handleNavigationClick('participantVisits')}
              >
                Visits
              </div>
            </div>
            <div className={'participant-view-tab-content ' + activeTab}>
              {buttonPressed ? screenMap.get(currentScreen) : screenMap.get('participantClientDetails')}
            </div>
          </div>
        </div>

        <div className="wizard-breadcrumb-container">
          <BreadCrumbTrail context={context} navigateTo={navigateTo} />
        </div>
      </div>
    </div>
  );
};
